import React, {useEffect, useRef, useState} from "react";
import GenericDialog from "@common/GenericDialog";
import axios from "@common/axios";
import {Slider} from "antd";

const EditDecisionPointDialog = props => {
    const {open, data, readOnly, onClose} = props;
    const [title, setTitle] = useState("");
    const [paths, setPaths] = useState([]);
    const [nextStep, setNextStep] = useState();
    const [showTooltip, setShowTooltip] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dialogBodyRef = useRef();
    const isDragging = useRef(false);

    useEffect(() => {
        if (data && open) {
            let newPathExists = false;
            let dpPaths = data.pathList
                .filter(path => path.startStepId === data.stepId)
                .map(path => {
                    if (path.pathId <= 0) newPathExists = true;
                    return {
                        pathId: path.pathId,
                        percentage: path.percentage,
                        nextStep: path.steps[0]
                    };
                });
            if (data.newPath && !newPathExists) {
                let largestGap = 0;
                let largestGapIndex = -1;
                findPathsGaps(dpPaths).forEach((gap, index) => {
                    if (gap > largestGap) {
                        largestGap = gap;
                        largestGapIndex = index;
                    }
                });

                let newPercentage;
                if (largestGap > 40) {
                    const previousPercentage = dpPaths[largestGapIndex - 1]?.percentage ?? 0;
                    newPercentage = previousPercentage + largestGap / 2;
                } else {
                    const lastPercentage = dpPaths.at(-1)?.percentage ?? 0;
                    newPercentage = lastPercentage + (100 - lastPercentage) / 2;
                }
                newPercentage = Math.round(newPercentage);
                dpPaths.push({
                    pathId: 0,
                    percentage: newPercentage
                });
            }

            const currentPath = data.pathList.find(path => path.steps.find(step => step.stepId === data.stepId));
            const currentStep = currentPath.steps.find(step => step.stepId === data.stepId);
            setTitle(currentStep.pathwayActivityName + " step: result paths");
            setNextStep(currentPath.steps.find(step => step.stepSeq === (currentStep?.stepSeq + 1)));
            dpPaths.sort((a, b) => parseFloat(a.percentage) - parseFloat(b.percentage));
            setPaths(dpPaths);
            setShowTooltip(true);
        }
    }, [data]);

    const findPathsGaps = dpPaths => {
        const gaps = [];
        let last = 0;
        for (const path of dpPaths) {
            gaps.push(path.percentage - last);
            last = path.percentage;
        }
        gaps.push(100 - last);
        return gaps;
    }

    const handleSliderChange = (newValues) => {
        if (readOnly) return;

        if (!isDragging.current) {
            const isHandleDragging = dialogBodyRef.current?.parentElement?.querySelectorAll(".ant-slider-handle-dragging").length > 0;
            if (isHandleDragging) isDragging.current = true;
        }

        const oldValues = paths.map(p => p.percentage);
        const movedFromValue = oldValues.find(ov => !newValues.includes(ov));
        const pathThatMoved = paths.find(p => p.percentage === movedFromValue);

        let movedToValue = newValues.find(nv => !oldValues.includes(nv));
        if (movedToValue === undefined) {
            movedToValue = newValues.find((v, i) => newValues.indexOf(v) !== i);
        }

        const duplicates = newValues.filter((item, index) => newValues.indexOf(item) !== index);
        if (duplicates.length > 0) {
            let updatedMovedToValue = null;
            const direction = movedToValue > movedFromValue ? 1 : -1;
            const loopStop = direction ? 99 : 1;
            for (let i = movedToValue; i !== loopStop; i += direction) {
                if (!oldValues.includes(i)) {
                    updatedMovedToValue = i;
                    break;
                }
            }
            movedToValue = updatedMovedToValue;
        }
        if (movedToValue) {
            const updatedPaths = paths.map(path =>
                path.pathId === pathThatMoved.pathId
                    ? {...path, percentage: movedToValue}
                    : path
            );
            updatedPaths.sort((a, b) => a.percentage - b.percentage);
            setPaths(updatedPaths);

            if (!isDragging.current) {
                requestAnimationFrame(() => {
                    const newIndex = updatedPaths.findIndex(p => p.pathId === pathThatMoved.pathId);
                    const thumbs = dialogBodyRef.current?.parentElement?.querySelectorAll(".ant-slider-handle");
                    if (thumbs?.[newIndex]) thumbs[newIndex].focus();
                });
            }
        }
    };

    return (
        <GenericDialog
            title={title}
            contentStyle={{
                width: 560
            }}
            open={open}
            onClose={() => {
                setShowTooltip(false);
                onClose();
            }}
            buttons={
                !readOnly ?
                    [{
                        name: 'Cancel',
                        extraProps: { type: 'text' },
                    }, {
                        name: "Update",
                        callback: async () => {
                            if (isSubmitting || readOnly) return;

                            try {
                                setIsSubmitting(true);

                                let updatedPathsCount = 0;
                                let params = {
                                    stepId: data.stepId
                                }
                                let newPathObj = null;

                                paths.forEach(path => {
                                    if (path.pathId > 0) {
                                        const currentPathPercentage = data.pathList.find(plPath => plPath.pathId === path.pathId).percentage;
                                        if (path.percentage !== currentPathPercentage) {
                                            const prefix = "paths[" + updatedPathsCount++ + "]";
                                            params[prefix + ".pathId"] = path.pathId;
                                            params[prefix + ".percentage"] = currentPathPercentage;
                                            params[prefix + ".updatePercentage"] = path.percentage;
                                        }
                                    } else {
                                        newPathObj = {
                                            pathId: data.pathId,
                                            stepId: data.stepId,
                                            completionPercentage: path.percentage
                                        };
                                    }
                                });

                                let response;
                                if (updatedPathsCount > 0) {
                                    response = await axios.post("/trail/updatePathDecisionPoint.action", params, {
                                        headers: { "Content-Type": "application/x-www-form-urlencoded"},
                                    });
                                } else if (newPathObj != null) {
                                    response = await axios.post("/trail/createBranchPath.action", newPathObj, {
                                        headers: { "Content-Type": "application/x-www-form-urlencoded"},
                                    });
                                } else {
                                    return;
                                }
                                if (response.data?.success) {
                                    data.onUpdateSuccess(response.data);
                                    onClose();
                                } else {
                                    console.error("Update unsuccessful:", response.data);
                                    displayToastMessage("Update failed", null, "error", undefined, 4000);
                                }
                            } finally {
                                setIsSubmitting(false);
                            }
                        }
                    }]: [{
                        name: 'Cancel',
                        extraProps: { type: 'text' },
                    }]
            }
        >
            <div ref={dialogBodyRef} className="padding-x--l padding-y--xl">
                {(() => {
                    const dpPaths = nextStep ? [{pathId: nextStep.pathId, nextStep: nextStep}, ...paths] : paths;
                    const gaps = findPathsGaps(paths);
                    return (
                        <div className="flexRow">
                            {dpPaths.map((path, i) => {
                                let icon;
                                let iconText = '';
                                if (path.pathId > 0) {
                                    icon = path.nextStep?.pathwayActivityIcon + "-icon";
                                    iconText = path.nextStep?.pathwayActivityName;
                                } else if (path.pathId === 0) {
                                    icon = "help-icon";
                                    iconText = "New path";
                                }
                                if (!iconText) return;
                                return (
                                    <div key={i} className="flexCentre ch-pw-dp-rangeBlk" style={{flex: `${gaps[i]} 0 0%`}}>
                                        <div className="flexColumn">
                                            <div className="flex">
                                                <div className="pw-b-s-icon">
                                                    {icon && (
                                                        <div className="icons-svg">
                                                            <svg viewBox="0 0 128 128" style={{margin: "-9"}}>
                                                                <use xlinkHref={`#${icon}`} />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex" style={{position: "relative"}}>
                                                <div className="pw-b-r-resourceTitle">{iconText}</div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )
                })()}
                <Slider
                    range
                    min={1}
                    max={99}
                    step={1}
                    tooltip={{
                        open: showTooltip,
                        placement: "bottom",
                        formatter: value => `>${value}`,
                        getPopupContainer: triggerNode => dialogBodyRef.current
                    }}
                    value={paths.map(path => path.percentage).sort((a, b) => a - b)}
                    disabled={readOnly}
                    onChange={handleSliderChange}
                    onChangeComplete={() => {isDragging.current = false}}
                    styles={{
                        track: {
                            background: "transparent",
                        }
                    }}
                />
            </div>
        </GenericDialog>
    )
}

export default EditDecisionPointDialog;